import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"
import { CheckCheck, List, LoaderIcon, Settings } from "lucide-react"

import BellBasicIcon from "@/assets/icons/bellBasic/medium.svg?react"
import BellRingingIcon from "@/assets/icons/bellRinging/medium.svg?react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ds/Popover"
import { Skeleton } from "@/components/ui/skeleton"
import { useNotificationScope } from "@/hooks/useNotificationHistory"
import { notificationRelativeTime, platformNotificationHref } from "@/lib/notificationLinks"
import { cn } from "@/lib/utils"
import routes from "@/routes/routes"
import {
  NotificationItem,
  useGetNotificationsQuery,
  useGetUnreadNotificationCountQuery,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationsReadMutation,
} from "@/store/notificationsAPI"

const UNREAD_POLLING_INTERVAL_MS = 60_000
const POPOVER_PAGE_SIZE = 10

function NotificationRow({
  notification,
  onSelect,
}: {
  notification: NotificationItem
  onSelect: (notification: NotificationItem) => void
}) {
  const { i18n } = useTranslation()
  const isUnread = !notification.readAt

  return (
    <button
      type="button"
      onClick={() => onSelect(notification)}
      className="flex w-full items-start gap-3 px-6 py-3 text-left transition-colors hover:bg-neutral-secondBg"
    >
      <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", isUnread ? "bg-brand-primary" : "bg-transparent")} />
      <span className="min-w-0">
        <span
          className={cn("block truncate text-body2", isUnread ? "font-medium text-neutral-title" : "text-neutral-text")}
        >
          {notification.title}
        </span>
        <span className="mt-0.5 line-clamp-2 block text-body2 text-neutral-text">{notification.body}</span>
        <span className="mt-1 block text-metadata text-neutral-text">
          {notificationRelativeTime(notification.createdAt, i18n.language) ?? "—"}
        </span>
      </span>
    </button>
  )
}

export default function HeaderNotificationBell() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [popoverOpen, setPopoverOpen] = useState(false)
  const scope = useNotificationScope()

  const unreadCountQuery = useGetUnreadNotificationCountQuery(scope, {
    skip: !scope,
    pollingInterval: UNREAD_POLLING_INTERVAL_MS,
  })
  const notificationsQuery = useGetNotificationsQuery(
    { limit: POPOVER_PAGE_SIZE, scope },
    { skip: !popoverOpen || !scope },
  )
  const [markRead] = useMarkNotificationsReadMutation()
  const [markAllRead, { isLoading: isMarkingAllRead }] = useMarkAllNotificationsReadMutation()

  const unreadCount = unreadCountQuery.currentData ?? 0
  const notifications = notificationsQuery.currentData?.items ?? []

  const handleSelect = async (notification: NotificationItem) => {
    if (!notification.readAt) {
      try {
        await markRead({ ids: [notification.id] }).unwrap()
      } catch {
        // Non-blocking
      }
    }
    const href = platformNotificationHref(notification.deepLink)
    if (href) {
      setPopoverOpen(false)
      navigate(href)
    }
  }

  const handleMarkAllRead = async () => {
    try {
      await markAllRead().unwrap()
    } catch {
      // Non-blocking
    }
  }

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger className="relative inline-flex items-center" aria-label={t("notifications.bellLabel")}>
        {/* The ringing bell is reserved for something actually waiting; a quiet
            inbox gets the plain bell, so the icon itself carries the signal
            rather than relying on the count badge alone. */}
        {unreadCount > 0 ? <BellRingingIcon className="h-6 w-6" /> : <BellBasicIcon className="h-6 w-6" />}
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger-primary px-1 text-[0.625rem] font-medium leading-none text-white">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={12}
        className="w-96 overflow-hidden rounded-2xl bg-white p-0 shadow-[0_-10px_24px_0_rgba(6,15,51,0.02),0_10px_24px_0_rgba(6,15,51,0.04)]"
      >
        <div className="flex items-center justify-between border-b px-6 py-4">
          <span className="text-body1 font-medium text-neutral-title">{t("notifications.bellLabel")}</span>
          <button
            type="button"
            onClick={handleMarkAllRead}
            disabled={isMarkingAllRead || unreadCount === 0}
            className="flex items-center gap-1 text-body2 text-brand-primary transition-colors hover:text-brand-hover disabled:cursor-not-allowed disabled:opacity-30"
          >
            {isMarkingAllRead ? <LoaderIcon className="h-4 w-4 animate-spin" /> : <CheckCheck className="h-4 w-4" />}
            {t("notifications.markAllRead")}
          </button>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {notificationsQuery.isLoading ? (
            <div className="flex flex-col gap-3 px-6 py-4">
              <Skeleton className="h-12 rounded bg-gray-200" />
              <Skeleton className="h-12 rounded bg-gray-200" />
              <Skeleton className="h-12 rounded bg-gray-200" />
            </div>
          ) : notificationsQuery.isError && !notifications.length ? (
            <div role="alert" className="p-4 text-body2">
              {t("notifications.inboxLoadError")}
              <button
                type="button"
                className="block text-brand-primary"
                disabled={notificationsQuery.isFetching}
                onClick={() => notificationsQuery.refetch()}
              >
                {t("notifications.retry")}
              </button>
            </div>
          ) : notifications.length ? (
            notifications.map((notification) => (
              <NotificationRow key={notification.id} notification={notification} onSelect={handleSelect} />
            ))
          ) : (
            <div className="px-6 py-8 text-center text-body2 text-neutral-text">{t("notifications.emptyInbox")}</div>
          )}
        </div>

        <div className="border-t">
          <Link
            to={routes["dashboard/notifications"]}
            onClick={() => setPopoverOpen(false)}
            className="flex w-full items-center gap-2 px-6 py-3 text-left text-body2 text-neutral-text transition-colors hover:bg-neutral-secondBg hover:text-neutral-title"
          >
            <List className="h-4 w-4" />
            {t("notifications.viewAll")}
          </Link>
          <Link
            to={routes["dashboard/settings/notifications"]}
            onClick={() => setPopoverOpen(false)}
            className="flex w-full items-center gap-2 border-t px-6 py-3 text-left text-body2 text-neutral-text transition-colors hover:bg-neutral-secondBg hover:text-neutral-title"
          >
            <Settings className="h-4 w-4" />
            {t("notifications.settingsLink")}
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  )
}
